import { graphql, Link, StaticQuery } from 'gatsby';
import React from 'react';
import sanitizeHtml from 'sanitize-html';
import styled from 'styled-components';

import { LocationProps, SubpageQueryResponse } from '../../interfaces';
import { AllMarkdownRemarkQueryResult } from '../../interfaces/AllMarkdownRemark';
import { Location } from '../../interfaces/Location';
import { Image, MarkdownFileNode } from '../../interfaces/MarkdownFileNode';
import { Description, Title } from '../../styles/CommonStyledComponents';
import { findKindergartenNodeByAllMarkdownRemark } from '../../utils/FindKindergartenNodeByAllMarkdownRemark';
import { linkUrl } from '../../utils/LinkUrl';
import { theme } from '../../utils/Theme';
import withLocation from '../../utils/WithLocation';
import { Button } from '../Button';
import { CircleImage } from '../CircleImage';

const Container = styled.div`
  width: 80%;
  margin: 0 auto;
  position: relative;
  display: flex;
  padding: 15px 0 40px 0;
  ${theme.mediaQuerySizes.bigLaptop} {
    width: 90%;
  }
  ${theme.mediaQuerySizes.tablet} {
    display: block;
  }
`;

const SectionSloganLeft = styled.div`
  min-width: 650px;
  height: 330px;
  position: relative;
  > div {
    position: absolute !important;
  }
  ${theme.mediaQuerySizes.bigLaptop} {
    min-width: 450px;
  }
  ${theme.mediaQuerySizes.smallLaptop} {
    min-width: 330px;
    margin-top: 45px;
  }
  ${theme.mediaQuerySizes.tablet} {
    margin-top: 0;
    height: 370px;
  }
  ${theme.mediaQuerySizes.phoneLight} {
    display: none;
  }
`;

const CircleImageWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 43%;
  width: 38%;
  z-index: 10;
  transition: all 0.2s ease-out;
  :hover {
    transform: scale(1.05);
    z-index: 20;
  }

  :first-child {
    top: 10%;
    left: 4%;
    width: 30%;
  }
  :last-child {
    top: 40%;
    left: 22%;
    width: 33%;
  }

  ${theme.mediaQuerySizes.bigLaptop} {
    top: 53px;
    left: 40%;
    width: 41%;
    :last-child {
      top: 49%;
      left: 21%;
      width: 34%;
    }
    :first-child {
      top: 27%;
      width: 31%;
    }
  }

  ${theme.mediaQuerySizes.smallLaptop} {
    :last-child {
      top: 41%;
    }
  }

  ${theme.mediaQuerySizes.tablet} {
    top: 0;
    left: 50%;
    width: 240px;
    :last-child {
      top: 143px;
      left: 31%;
      width: 215px;
    }
    :first-child {
      top: 36px;
      left: 15%;
      width: 190px;
    }
  }
  ${theme.mediaQuerySizes.phone} {
    left: 51%;
    :first-child {
      left: 4%;
    }
    :last-child {
      left: 26%;
    }
  }
`;

const SectionSloganRight = styled.div`
  width: 55%;
  position: relative;
  h1 {
    margin-left: 0;
    text-align: left;
  }
  > div {
    margin-left: 0;
  }
  a {
    display: block;
    margin-top: 40px;
  }
  ${theme.mediaQuerySizes.bigLaptop} {
    width: 65%;
  }
  ${theme.mediaQuerySizes.tablet} {
    width: 100%;
    a {
      display: flex;
      text-decoration: none;
      > button {
        margin: 0 auto;
      }
    }
  }
`;

interface SloganProps {
  location: LocationProps;
}

interface DataProps extends AllMarkdownRemarkQueryResult<MarkdownFileNode>, SubpageQueryResponse, Location {}

const query = graphql`
  query {
    allMarkdownRemark(filter: { fileAbsolutePath: { regex: "/home-slogan/" } }) {
      nodes {
        fileAbsolutePath
        frontmatter {
          title
          images {
            image {
              childImageSharp {
                fluid(maxWidth: 300, maxHeight: 300, quality: 80) {
                  ...GatsbyImageSharpFluid_withWebp_noBase64
                }
              }
            }
          }
        }
        html
      }
    }
    subpagesJson(key: { eq: "about" }) {
      url
    }
  }
`;

const Slogan = (props: SloganProps) => {
  return (
    <StaticQuery
      query={query}
      render={(data: DataProps) => {
        const colors = [theme.colors.violet, theme.colors.blue, theme.colors.pink];
        const homeSlogan = findKindergartenNodeByAllMarkdownRemark(data.allMarkdownRemark.nodes, props.location);
        return (
          <Container>
            <SectionSloganLeft>
              {homeSlogan?.frontmatter?.images?.map((photo: Image | undefined, index: number) => (
                <CircleImageWrapper key={index}>
                  <CircleImage
                    key={index}
                    image={photo?.image.childImageSharp.fluid}
                    borderColor={colors[index]}
                    borderSize={8}
                  />
                </CircleImageWrapper>
              ))}
            </SectionSloganLeft>
            <SectionSloganRight>
              <Title>{homeSlogan?.frontmatter?.title}</Title>
              <Description
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(homeSlogan?.html || ''),
                }}
              ></Description>
              <Link to={linkUrl(props.location.pathname, data.subpagesJson.url)}>
                <Button>Więcej</Button>
              </Link>
            </SectionSloganRight>
          </Container>
        );
      }}
    />
  );
};

export default withLocation(Slogan);
